/**
 * FilePreviewModal — 纯本地绝密级全局文件预览弹窗
 *
 * Architecture 3.0 Phase 4: 微内核重构
 * - 渲染逻辑完全委托给 registry/renderers.tsx 注册表
 * - 本文件不再包含任何格式判断的 if-else / switch-case
 * - 新增格式只需在 registry 中追加一行 registerRenderer 即可
 *
 * 设计体系：完全遵循 Lawhelper 的 Notion 风格设计令牌
 * 安全：零外网请求，绝对私有化
 */

import React, { useEffect, useState, useCallback, Suspense } from 'react';
import { X, FileText, Loader2, FileSpreadsheet, FileType, Image, Music, Film } from 'lucide-react';
import { getRenderer } from '../registry/renderers';
import type { RendererProps } from '../registry/renderers';

// ─── Types ─────────────────────────────────

export interface PreviewFile {
  /** 文件名（含扩展名） */
  name: string;
  /** 文件数据源：可以是 File/Blob 对象, base64 字符串, 或远程 URL */
  data: File | Blob | string;
  /** 可选：来源标记 */
  source?: string;
}

interface FilePreviewModalProps {
  file: PreviewFile | null;
  onClose: () => void;
}

// ─── 工具函数 ──────────────────────────────

const getExt = (name: string): string =>
  (name.split('.').pop() || '').toLowerCase();

const toBlob = async (data: File | Blob | string): Promise<Blob> => {
  if (data instanceof Blob) return data;
  if (typeof data === 'string') {
    if (data.startsWith('http') || data.startsWith('/') || data.startsWith('data:')) {
      const res = await fetch(data);
      return await res.blob();
    }
    // 纯 base64
    const binary = atob(data);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes]);
  }
  return new Blob([]);
};

/** 格式化文件大小 */
const formatSize = (blob: Blob): string => {
  const bytes = blob.size;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** 获取格式图标 */
const getFormatIcon = (ext: string): React.ReactNode => {
  if (ext === 'pdf') return <FileText className="w-4 h-4" />;
  if (['docx', 'doc'].includes(ext)) return <FileType className="w-4 h-4" />;
  if (['xlsx', 'xls', 'csv'].includes(ext)) return <FileSpreadsheet className="w-4 h-4" />;
  if (['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(ext)) return <Image className="w-4 h-4" />;
  if (['mp3', 'wav', 'ogg', 'aac', 'flac', 'm4a'].includes(ext)) return <Music className="w-4 h-4" />;
  if (['mp4', 'webm', 'mov', 'avi', 'mkv'].includes(ext)) return <Film className="w-4 h-4" />;
  return <FileText className="w-4 h-4" />;
};

// ─── 统一加载占位组件 ──────────────────────

const LoadingPlaceholder: React.FC<{ label?: string }> = ({ label = '正在解析文件...' }) => (
  <div className="flex-1 flex flex-col items-center justify-center gap-3" style={{ color: 'var(--notion-text-secondary, #787774)' }}>
    <Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--notion-accent, #2383E2)' }} />
    <span className="text-sm font-medium">{label}</span>
  </div>
);

// ─── 主弹窗组件 ────────────────────────────

export const FilePreviewModal: React.FC<FilePreviewModalProps> = ({ file, onClose }) => {
  const [blob, setBlob] = useState<Blob | null>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Escape 键关闭
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  // 数据预处理：将任意来源转为 Blob + ObjectURL
  useEffect(() => {
    if (!file) {
      setBlob(null);
      setObjectUrl(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    let url: string | null = null;

    toBlob(file.data)
      .then((b) => {
        setBlob(b);
        url = URL.createObjectURL(b);
        setObjectUrl(url);
      })
      .catch(() => {
        setBlob(null);
        setObjectUrl(null);
      })
      .finally(() => setLoading(false));

    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [file]);

  // 预加载：弹窗打开时，根据扩展名预载对应 chunk（与 blob 准备并行）
  useEffect(() => {
    if (!file) return;
    const ext = getExt(file.name);
    const entry = getRenderer(ext);
    if (entry) entry.load(); // 预触发 chunk 下载
  }, [file]);

  // ─── 渲染器路由（完全由注册表驱动） ─────
  const renderContent = useCallback(() => {
    if (!file || !blob || !objectUrl) return null;
    const ext = getExt(file.name);

    const entry = getRenderer(ext);
    if (entry) {
      const LazyComponent = React.lazy(entry.load);
      return (
        <Suspense fallback={<LoadingPlaceholder label={`正在加载 ${entry.label}...`} />}>
          <LazyComponent blob={blob} url={objectUrl} name={file.name} />
        </Suspense>
      );
    }

    // 未注册格式 — 显示不支持提示
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3" style={{ color: 'var(--notion-text-muted, #9B9A97)' }}>
        <FileText className="w-12 h-12" />
        <p className="text-sm">暂不支持预览 .{ext} 格式的文件</p>
        <p className="text-xs">请下载后使用本地应用程序打开</p>
      </div>
    );
  }, [file, blob, objectUrl]);

  if (!file) return null;

  const ext = getExt(file.name);

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center">
      {/* 遮罩层 — 与邮件弹窗统一的 bg-black/50 */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* 弹窗主体 — Notion 风格容器 */}
      <div
        className="relative flex flex-col overflow-hidden w-[92vw] max-w-6xl h-[90vh] animate-fade-in"
        style={{
          background: 'var(--notion-bg, #FFFFFF)',
          borderRadius: '12px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          border: '1px solid var(--notion-border, #E9E9E7)',
        }}
      >
        {/* 顶部导航栏 — Notion 设计语言 */}
        <div
          className="flex items-center justify-between px-5 py-3 flex-shrink-0"
          style={{
            borderBottom: '1px solid var(--notion-border, #E9E9E7)',
            background: 'var(--notion-bg-tertiary, #FAFAFA)',
          }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <span style={{ color: 'var(--notion-text-secondary, #787774)' }}>
              {getFormatIcon(ext)}
            </span>
            <span
              className="text-sm font-semibold truncate max-w-[400px]"
              style={{ color: 'var(--notion-text, #37352F)' }}
            >
              {file.name}
            </span>
            {blob && (
              <span
                className="text-xs px-2 py-0.5 rounded-full"
                style={{
                  color: 'var(--notion-text-muted, #9B9A97)',
                  background: 'var(--notion-bg-secondary, #F7F6F3)',
                }}
              >
                {formatSize(blob)}
              </span>
            )}
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-colors"
            style={{ color: 'var(--notion-text-secondary, #787774)' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--notion-bg-secondary, #F7F6F3)';
              e.currentTarget.style.color = 'var(--notion-text, #37352F)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = 'var(--notion-text-secondary, #787774)';
            }}
            title="关闭 (Esc)"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* 内容渲染区 */}
        {loading ? (
          <LoadingPlaceholder />
        ) : (
          renderContent()
        )}
      </div>
    </div>
  );
};

export default FilePreviewModal;
